import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Typography, Alert, Modal, Paper } from "@mui/material";
import { Tooltip } from "@mui/material";
import { FileInput } from "../../components/common/FileInput";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { uploadFileToS3Complete } from "../../api/s3";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import ImagePreview from "../../components/images/ImagePreview";
import { useImageValidation } from "../../hooks/useImageValidation";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as EyeIcon } from "../../assets/images/eye-line.svg";
import { ReactComponent as InformationIcon } from "../../assets/images/information-line.svg";
import AddButton from "../../components/ui/AddButton";
import Breadcrumb from "../../components/common/Breadcrumb";
import NavigationTags from "../../components/common/NavigationTags";
import { generateHubFoldersBreadcrumbs } from "../../utils/breadcrumbHelpers";
import { TextField, NumberField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import {
    getSingleTrackFirstFilters,
    createSingleTrackFirstFilter,
    updateSingleTrackFirstFilter,
    deleteSingleTrackFirstFilter,
    getSingleTrackFolders,
} from "../../api/company";
import ButtonComponent from "../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { GetSingleTrackFirstFiltersResponse, GetSingleTrackFoldersResponse } from "../../types/requestsOutputs";
import { useGlobalState } from "../../context/useGlobalState";
import { useNotification } from "../../context/NotificationContext";
import DeleteModal from "../../components/modals/DeleteModal";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import Icon from "../../components/common/Icon";
import hubStyles from "./Hub.module.css";
import pageHeader from "../../components/layouts/PageHeader.module.css";
import studio from "../../components/modals/StudioModal.module.css";
import stepStyles from "../track/create/createStep.module.css";
interface FolderFormValues {
    name: string;
    coverImage?: string;
    position?: number;
}

// Use the actual API response type instead of local interface
type Folder = GetSingleTrackFirstFiltersResponse[0];
type Hub = GetSingleTrackFoldersResponse[0];

const FolderForm = ({
    onClose,
    isEdit = false,
    initialValues,
    originalFolder,
    hub,
}: {
    onClose: () => void;
    isEdit?: boolean;
    initialValues?: FolderFormValues;
    originalFolder?: Folder | null;
    hub?: Hub;
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue, errors, touched, isSubmitting, submitForm } = useFormikContext<FolderFormValues>();
    const { data, loading } = useGlobalState();
    const imageValidation = useImageValidation({
        type: "hubFolderCover",
        imageUrl: values.coverImage || undefined,
    });

    const handleIconFileSelected = useCallback(
        async (files: File | File[]) => {
            const file = Array.isArray(files) ? files[0] : files;
            if (file) {
                const token = localStorage.getItem("token");
                if (!token) return;
                await imageValidation.validateImage(file);
                uploadFileToS3Complete({ file, isPublic: true, isFolder: false }, token)
                    .then((result) => setFieldValue("coverImage", result))
                    .catch(console.error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, imageValidation.validateImage],
    );

    const language = data.languagesAI.find((lang) => lang.value === hub?.originLanguageId)?.label;

    if (loading) {
        return (
            <div className="form-loading-container">
                <div className={hubStyles.formLoadingSpinner}></div>
                <p className="form-loading-text">{t("loadingLanguages")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div className={studio.folderForm}>
                <h2 className={studio.dialogTitle}>{isEdit ? t("editFolder") : t("createFolder")}</h2>

                {/* Name */}
                <FormGroup
                    title={t("name")}
                    subtitle={t("nameWithOriginLanguage", { language: language })}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                    required={!isEdit}
                >
                    <Field name="name" component={TextField} placeholder={t("enterFolderName")} />
                    {touched.name && errors.name && (
                        <Alert severity="error" className="form-error-alert">
                            {errors.name}
                        </Alert>
                    )}
                </FormGroup>

                {/* Cover Image */}
                <FormGroup
                    title={t("folderIcon")}
                    subtitle={t("folderIconDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <ImageSpecsBadges spec="hubFolderCover" warnings={imageValidation.warnings} />
                    <div className={`${hubStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handleIconFileSelected}
                            showButton={true}
                            buttonVariant="outline"
                            placeholder={t("dragAndDropIconHereOrClickToSelect")}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_200_KB}
                            maxSizeLabel={t("imageMaxSize200kb")}
                        />
                    </div>

                    {values.coverImage && (
                        <div className={hubStyles.iconPreviewContainer}>
                            <Typography variant="subtitle2" className={hubStyles.previewLabel}>
                                {t("preview")}:
                            </Typography>
                            <ImagePreview
                                key={values.coverImage}
                                id={values.coverImage}
                                photo={values.coverImage}
                                onDelete={() => setFieldValue("coverImage", undefined)}
                            />
                        </div>
                    )}
                </FormGroup>

                {/* Position */}
                <FormGroup
                    title={t("position")}
                    subtitle={t("positionDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <Field name="position" component={NumberField} placeholder={t("enterPosition")} min={0} />
                </FormGroup>

                {/* Action Buttons */}
                <div className={studio.dialogActions}>
                    <ButtonComponent
                        variant="cancel"
                        shape="pill"
                        label={t("cancel")}
                        onClick={onClose}
                        disabled={isSubmitting}
                    />
                    <Tooltip
                        title={
                            Object.keys(errors).length > 0
                                ? t("formIncompleteTooltip", { fields: Object.values(errors).join(", ") })
                                : ""
                        }
                        arrow
                    >
                        <span>
                            <ButtonComponent
                                variant="primary"
                                shape="pill"
                                label={isEdit ? t("updateFolder") : t("createFolder")}
                                onClick={submitForm}
                                disabled={isSubmitting || Object.keys(errors).length > 0}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>
        </Form>
    );
};

const HubFolders: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const hubId = searchParams.get("hubId");

    const [folders, setFolders] = useState<GetSingleTrackFirstFiltersResponse>([]);
    const [hub, setHub] = useState<Hub | null>(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
    const [selectedFolderToDelete, setSelectedFolderToDelete] = useState<Folder | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const { showNotification } = useNotification();

    // Dynamic Yup validation schema
    const getValidationSchema = () => {
        return Yup.object({
            name: Yup.string().required(t("folderNameRequired")),
            position: Yup.number().integer(t("positionMustBeInteger")).min(0, t("positionMustBePositive")),
        });
    };

    // Initial values for create
    const initialValues: FolderFormValues = {
        name: "",
        coverImage: "",
        position: 0,
    };

    // Fetch hub and folders on component mount / language change
    useEffect(() => {
        if (hubId) {
            fetchHub();
            fetchFolders();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubId, i18n.language]);

    const fetchHub = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await getSingleTrackFolders({ token, data: { language: i18n.language || "en" } });
            const currentHub = response.find((h) => h.id === hubId);
            if (currentHub) {
                setHub(currentHub);
            } else {
                setHub(null);
            }
        } catch (error) {
            console.error("Error fetching hub:", error);
        }
    };

    const fetchFolders = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token || !hubId) {
                setLoading(false);
                return;
            }

            const response = await getSingleTrackFirstFilters({
                token,
                data: {
                    companySingleTrackFolderId: hubId,
                    language: i18n.language || "en",
                },
            });
            if (response) {
                setFolders(response);
            }
        } catch (error) {
            console.error("Error fetching folders:", error);
            showNotification({
                message: t("errorFetchingFolders"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateFolder = async (values: FolderFormValues, formikHelpers: FormikHelpers<FolderFormValues>) => {
        const token = localStorage.getItem("token");
        if (!token || !hubId) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        try {
            await createSingleTrackFirstFilter({
                token,
                data: {
                    companySingleTrackFolderId: hubId,
                    name: values.name,
                    coverImage: values.coverImage,
                    position: values.position,
                },
            });
            showNotification({
                message: t("folderCreatedSuccessfully"),
                type: "success",
            });
            setModalOpen(false);
            fetchFolders();
            formikHelpers.resetForm();
        } catch (error) {
            console.error("Error creating folder:", error);
            showNotification({
                message: t("errorCreatingFolder"),
                type: "error",
            });
        }
    };

    const handleUpdateFolder = async (values: FolderFormValues, formikHelpers: FormikHelpers<FolderFormValues>) => {
        if (!editingFolder) return;

        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        try {
            // Build update data with only changed fields
            const updateData: any = { firstFilterId: editingFolder.id };

            // Only include fields that have changed
            if (values.name !== editingFolder.internalName) {
                updateData.newName = values.name;
            }
            if ((values.coverImage || "") !== (editingFolder.coverImage || "")) {
                updateData.coverImage = values.coverImage;
            }
            if (values.position !== editingFolder.position) {
                updateData.position = values.position;
            }

            await updateSingleTrackFirstFilter({
                token,
                data: updateData,
            });
            showNotification({
                message: t("folderUpdatedSuccessfully"),
                type: "success",
            });
            setModalOpen(false);
            setEditingFolder(null);
            fetchFolders();
            formikHelpers.resetForm();
        } catch (error) {
            console.error("Error updating folder:", error);
            showNotification({
                message: t("errorUpdatingFolder"),
                type: "error",
            });
        }
    };

    const handleDeleteFolder = async (folderId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Store the folder being deleted for potential revert
        const folderToDelete = folders.find((f) => f.id === folderId);
        if (!folderToDelete) return;

        // Optimistic update: immediately remove from UI
        setFolders((prevFolders) => prevFolders.filter((f) => f.id !== folderId));

        try {
            await deleteSingleTrackFirstFilter({
                token,
                data: { firstFilterId: folderId },
            });
            showNotification({
                message: t("folderDeletedSuccessfully"),
                type: "success",
            });
        } catch (error) {
            console.error("Error deleting folder:", error);
            showNotification({
                message: t("errorDeletingFolder"),
                type: "error",
            });

            // Revert the optimistic update on error by adding the folder back
            setFolders((prevFolders) => [...prevFolders, folderToDelete]);
        }
    };

    const openEditModal = (folder: Folder) => {
        setEditingFolder(folder);
        setModalOpen(true);
    };

    const openCreateModal = () => {
        setEditingFolder(null);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingFolder(null);
    };

    const handleManageContent = (folder: Folder) => {
        navigate(`/hubs/folders/categories?folderId=${folder.id}&hubId=${hubId}`);
    };

    const handleViewPage = (folder: Folder) => {
        const hubToken = hub?.oTPToken?.[0]?.token;
        if (!hubToken) return;
        const url = `https://hub.rewindstories.com/${hubId}/${folder.id}`;
        window.open(url, "_blank");
    };

    const handleToggleStatus = async (folder: Folder) => {
        const token = localStorage.getItem("token");
        if (!token) {
            showNotification({
                message: t("noTokenFound"),
                type: "error",
            });
            return;
        }

        // Optimistic update: immediately update the UI
        const newStatus = !folder.status;
        setFolders((prevFolders) => prevFolders.map((f) => (f.id === folder.id ? { ...f, status: newStatus } : f)));

        try {
            await updateSingleTrackFirstFilter({
                token,
                data: {
                    firstFilterId: folder.id,
                    status: newStatus,
                },
            });
            // No need to refetch since we already updated optimistically
        } catch (error) {
            console.error("Error updating folder status:", error);
            showNotification({
                message: t("errorUpdatingFolderStatus"),
                type: "error",
            });

            // Revert the optimistic update on error
            setFolders((prevFolders) =>
                prevFolders.map((f) => (f.id === folder.id ? { ...f, status: folder.status } : f)),
            );
        }
    };

    const sortedFolders = useMemo(() => {
        return [...folders].sort((a, b) => {
            const posA = a.position || 0;
            const posB = b.position || 0;

            // Case 1: both have a non-zero position → sort numerically
            if (posA !== 0 && posB !== 0) {
                if (posA !== posB) {
                    return posA - posB;
                }
                // same position → sort alphabetically
                return a.internalName.localeCompare(b.internalName);
            }

            // Case 2: one has position ≠ 0, the other is 0 → non-zero comes first
            if (posA !== 0 && posB === 0) return -1;
            if (posB !== 0 && posA === 0) return 1;

            // Case 3: both are 0 → sort alphabetically
            return a.internalName.localeCompare(b.internalName);
        });
    }, [folders]);

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            <div className={hubStyles.pageHeader}>
                {hub && (
                    <Breadcrumb
                        classes={{
                            nav: hubStyles.breadcrumbNav,
                            separator: hubStyles.breadcrumbSeparator,
                            link: hubStyles.breadcrumbLink,
                            text: hubStyles.breadcrumbText,
                            active: hubStyles.breadcrumbActive,
                        }}
                        items={generateHubFoldersBreadcrumbs({ hubId, hubName: hub.internalName, t })}
                    />
                )}
                {hub && (
                    <div className={pageHeader.titleSection}>
                        <div className={pageHeader.content}>
                            <h2 className={hubStyles.settingsTitle}>
                                {hub.internalName}: {t("configureHub")}
                            </h2>
                            <div className={hubStyles.pageSubtitleWithInfo}>
                                <Typography variant="body2" className={hubStyles.pageSubtitle}>
                                    {t("manageFoldersDescription")}
                                </Typography>
                                <Tooltip title={t("foldersExplanationTooltip")} arrow placement="right">
                                    <InformationIcon className={hubStyles.infoIcon} />
                                </Tooltip>
                            </div>
                        </div>
                        <AddButton
                            labeledClassName={hubStyles.addButtonLabeled}
                            onClick={openCreateModal}
                            tooltip={t("createFolder")}
                            label={t("newPage")}
                        />
                    </div>
                )}
                <NavigationTags
                    classes={{
                        container: hubStyles.navigationTags,
                        tag: hubStyles.navigationTag,
                        tagActive: hubStyles.navigationTagActive,
                        tagDisabled: hubStyles.navigationTagDisabled,
                        arrow: hubStyles.navigationTagArrow,
                    }}
                    tags={[
                        { label: t("hub"), path: "/hubs" },
                        { label: t("page"), isActive: true },
                        { label: t("category") },
                        { label: t("content") },
                    ]}
                />
            </div>

            {loading ? (
                <div className="form-loading-container">
                    <div className={hubStyles.formLoadingSpinner}></div>
                    <p className="form-loading-text">{t("loadingFolders")}</p>
                </div>
            ) : folders.length === 0 ? (
                <div className={hubStyles.emptyState}>
                    <div className={hubStyles.emptyStateIcon} aria-hidden>
                        <Icon name="ic-grid" className="ic" />
                    </div>
                    <Typography variant="h6" className={hubStyles.emptyStateTitle}>
                        {t("noFoldersYet")}
                    </Typography>
                    <Typography variant="body2" className={hubStyles.emptyStateText}>
                        {t("noFoldersDescription")}
                    </Typography>
                    <AddButton
                        labeledClassName={hubStyles.emptyStateButton}
                        onClick={openCreateModal}
                        tooltip={t("createFolder")}
                        label={t("newPage")}
                    />
                </div>
            ) : (
                <div className={hubStyles.foldersGrid}>
                    {sortedFolders.map((folder) => (
                        <div
                            key={folder.id}
                            className={hubStyles.folderCard}
                            onClick={() => handleManageContent(folder)}
                        >
                            {/* Page type icon */}
                            <div className={hubStyles.rowTypeIcon}>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect x="3" y="3" width="7" height="7" />
                                    <rect x="14" y="3" width="7" height="7" />
                                    <rect x="14" y="14" width="7" height="7" />
                                    <rect x="3" y="14" width="7" height="7" />
                                </svg>
                            </div>

                            {/* Info */}
                            <div className={hubStyles.rowInfo}>
                                <p className={hubStyles.rowName}>{folder.internalName}</p>
                                <p className={hubStyles.rowMeta}>
                                    {hub?.internalName}
                                    {folder.position ? ` · ${t("position")} ${folder.position}` : ""}
                                    {folder.categoryCount !== undefined
                                        ? ` · ${folder.categoryCount} ${folder.categoryCount === 1 ? t("category") : t("categories")}`
                                        : ""}
                                </p>
                            </div>

                            {/* Status toggle */}
                            <div
                                className={hubStyles.statusToggleContainer}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <span
                                    className={`${hubStyles.statusLabel}${folder.status ? ` ${hubStyles.statusLabelActive}` : ""}`}
                                >
                                    {folder.status ? t("active") : t("inactive")}
                                </span>
                                <ToggleSwitch
                                    checked={folder.status || false}
                                    onChange={() => handleToggleStatus(folder)}
                                />
                            </div>

                            {/* Separator */}
                            <div className={hubStyles.rowSeparator} />

                            {/* Actions: eye + pen + trash */}
                            <div
                                className={hubStyles.rowActions}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <Tooltip title={t("viewPage")} arrow>
                                    <span>
                                        <EyeIcon
                                            className={hubStyles.mutedIconButton}
                                            style={{
                                                opacity: hub?.oTPToken?.[0]?.token ? 1 : 0.35,
                                                cursor: hub?.oTPToken?.[0]?.token ? "pointer" : "not-allowed",
                                            }}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                if (hub?.oTPToken?.[0]?.token) handleViewPage(folder);
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title={t("edit")} arrow>
                                    <EditIcon
                                        className={hubStyles.hubSettingsIconButton}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            openEditModal(folder);
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={t("delete")} arrow>
                                    <DeleteIcon
                                        className={hubStyles.hubDeleteIconButton}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            setSelectedFolderToDelete(folder);
                                            setIsDeleteModalOpen(true);
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    ))}
                    {selectedFolderToDelete && (
                        <DeleteModal
                            isOpen={isDeleteModalOpen}
                            onClose={() => {
                                setIsDeleteModalOpen(false);
                                setSelectedFolderToDelete(null);
                            }}
                            onConfirm={() => {
                                handleDeleteFolder(selectedFolderToDelete.id);
                                setSelectedFolderToDelete(null);
                            }}
                            message={t("deleteFolderConfirmation")}
                            title={t("deleteFolder")}
                        />
                    )}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Modal open={modalOpen} onClose={closeModal} className={studio.folderOverlay}>
                <Paper className={studio.folderContent}>
                    <Formik
                        initialValues={
                            editingFolder
                                ? {
                                      name: editingFolder.internalName,
                                      coverImage: editingFolder.coverImage || "",
                                      position: editingFolder.position || 0,
                                  }
                                : initialValues
                        }
                        validationSchema={getValidationSchema()}
                        onSubmit={editingFolder ? handleUpdateFolder : handleCreateFolder}
                        enableReinitialize
                        validateOnMount
                    >
                        <FolderForm
                            onClose={closeModal}
                            isEdit={!!editingFolder}
                            originalFolder={editingFolder}
                            initialValues={
                                editingFolder
                                    ? {
                                          name: editingFolder.internalName,
                                          coverImage: editingFolder.coverImage || "",
                                          position: editingFolder.position || 0,
                                      }
                                    : undefined
                            }
                            hub={hub ?? undefined}
                        />
                    </Formik>
                </Paper>
            </Modal>
        </div>
    );
};

export default HubFolders;
